/**
 * Schlanker LiveKit-Publisher für „Quelle"-Panels (Web-Einspielung).
 *
 * Eine Quelle wählt ein Audio-Eingangsgerät (oder den Test-Ton) und publisht
 * EINEN Track `pgm:<panelId>` mit der Identity `source-<panelId>`. Das Backend
 * registriert daraus automatisch einen externen Port
 * (`source-<panelId>::pgm:<panelId>`), den andere Panels per „Listen Port"-
 * Button abhören können – exakt wie das Desktop-PGM, nur im Browser.
 */
import { Room, RoomEvent, type LocalAudioTrack } from 'livekit-client';
import { api } from './api.js';

export const TEST_TONE_DEVICE_ID = '__livepuls_testtone__';

interface TokenResponse {
  url: string;
  token: string;
  iceServers?: Array<{ urls: string | string[]; username?: string; credential?: string }>;
}

export class SourceEngine {
  room: Room | null = null;
  level = 0;
  error: string | null = null;
  onChange: () => void = () => {};

  private ctx: AudioContext | null = null;
  private rawStream: MediaStream | null = null;
  private testOsc: OscillatorNode | null = null;
  private gainNode: GainNode | null = null;
  private analyser: AnalyserNode | null = null;
  private dest: MediaStreamAudioDestinationNode | null = null;
  private localTrack: LocalAudioTrack | null = null;
  private raf: number | null = null;
  private deviceId = '';
  private gain = 1;
  private panelId = '';
  private panelName = '';

  get connected(): boolean {
    return this.room?.state === 'connected';
  }

  /** Verbindet, startet Capture und publisht den Quelle-Track. */
  async connect(panelId: string, panelName: string, deviceId: string, gain: number): Promise<void> {
    this.panelId = panelId;
    this.panelName = panelName;
    this.deviceId = deviceId;
    this.gain = gain;
    await this.startCapture();

    const identity = `source-${panelId}`;
    const tok = await api.post<TokenResponse>('/api/v1/livekit/token', {
      identity,
      role: 'publisher-subscriber',
    });
    const iceServers: RTCIceServer[] = (tok.iceServers ?? []).map((s) => ({
      urls: s.urls,
      ...(s.username !== undefined ? { username: s.username } : {}),
      ...(s.credential !== undefined ? { credential: s.credential } : {}),
    }));
    const room = new Room({ adaptiveStream: false, dynacast: false });
    room.on(RoomEvent.Disconnected, () => this.onChange());
    room.on(RoomEvent.Connected, () => this.onChange());
    await room.connect(tok.url, tok.token, {
      ...(iceServers.length > 0 ? { rtcConfig: { iceServers } } : {}),
    });
    this.room = room;
    // Metadata → Backend leitet daraus den Port-Anzeigenamen ab.
    try {
      await room.localParticipant.setMetadata(
        JSON.stringify({ slots: [{ slotId: panelId, kind: 'pgm', label: panelName }] }),
      );
    } catch {
      /* nice-to-have */
    }
    await this.publish();
    this.onChange();
  }

  /** Gerät/Test-Ton wechseln (auch im laufenden Betrieb). */
  async setDevice(deviceId: string): Promise<void> {
    this.deviceId = deviceId;
    const wasConnected = this.connected;
    await this.unpublish();
    await this.releaseCapture();
    await this.startCapture();
    if (wasConnected) await this.publish();
    this.onChange();
  }

  setGain(gain: number): void {
    this.gain = gain;
    if (this.gainNode) this.gainNode.gain.value = gain;
  }

  async disconnect(): Promise<void> {
    await this.unpublish();
    await this.releaseCapture();
    if (this.room) {
      await this.room.disconnect();
      this.room = null;
    }
    this.onChange();
  }

  private async startCapture(): Promise<void> {
    if (this.dest && (this.rawStream || this.testOsc)) return;
    const ctx = this.ctx ?? new AudioContext({ sampleRate: 48000 });
    this.ctx = ctx;
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        /* */
      }
    }
    const gain = ctx.createGain();
    gain.gain.value = this.gain;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    const dest = ctx.createMediaStreamDestination();

    if (this.deviceId === TEST_TONE_DEVICE_ID) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 1000;
      const toneGain = ctx.createGain();
      toneGain.gain.value = 0.15;
      osc.connect(toneGain);
      toneGain.connect(gain);
      osc.start();
      this.testOsc = osc;
    } else {
      const constraints: MediaTrackConstraints = {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        ...(this.deviceId ? { deviceId: { exact: this.deviceId } } : {}),
      };
      const stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
      this.rawStream = stream;
      const src = ctx.createMediaStreamSource(stream);
      src.connect(gain);
    }
    gain.connect(analyser);
    gain.connect(dest);
    this.gainNode = gain;
    this.analyser = analyser;
    this.dest = dest;
    this.error = null;
    this.startMeter();
  }

  private async publish(): Promise<void> {
    if (!this.room || this.room.state !== 'connected') return;
    if (this.localTrack) return;
    if (!this.dest) await this.startCapture();
    const track = this.dest?.stream.getAudioTracks()[0];
    if (!track) throw new Error('Capture lieferte keinen Track');
    const pub = await this.room.localParticipant.publishTrack(track, {
      name: `pgm:${this.panelId}`,
      dtx: false,
      red: false,
    });
    this.localTrack = (pub.track as LocalAudioTrack | undefined) ?? null;
  }

  private async unpublish(): Promise<void> {
    if (this.localTrack && this.room?.state === 'connected') {
      await this.room.localParticipant
        .unpublishTrack(this.localTrack.mediaStreamTrack)
        .catch(() => {});
    }
    this.localTrack = null;
  }

  private async releaseCapture(): Promise<void> {
    if (this.testOsc) {
      try {
        this.testOsc.stop();
        this.testOsc.disconnect();
      } catch {
        /* */
      }
      this.testOsc = null;
    }
    if (this.rawStream) {
      for (const t of this.rawStream.getTracks()) {
        try {
          t.stop();
        } catch {
          /* */
        }
      }
      this.rawStream = null;
    }
    try {
      this.gainNode?.disconnect();
      this.analyser?.disconnect();
      this.dest?.disconnect();
    } catch {
      /* */
    }
    this.gainNode = null;
    this.analyser = null;
    this.dest = null;
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    this.level = 0;
  }

  private startMeter(): void {
    if (!this.analyser) return;
    const buf = new Float32Array(this.analyser.fftSize);
    const tick = (): void => {
      if (!this.analyser) return;
      this.analyser.getFloatTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) {
        const a = Math.abs(v);
        if (a > peak) peak = a;
      }
      this.level = peak;
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }
}
